import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { MONTH_NAMES } from './format';

const FILL_HEADER = { patternType: 'solid', fgColor: { rgb: '5AB031' } };
const FILL_WEEKEND = { patternType: 'solid', fgColor: { rgb: 'EEEEEE' } };
const FONT_HEADER = { bold: true, color: { rgb: 'FFFFFF' } };
const toHours = (minutes) => (minutes ? Math.round((minutes / 60) * 100) / 100 : '');

/**
 * Foglio ore: righe = entità (progetto o persona), colonne = giorni + totale (+ colonne extra).
 * rows: [{ labels: string[], days: { [day]: minutes }, extra?: { [header]: minutes } }]
 */
function buildSheet(labelHeaders, rows, monthDays, extraHeaders = []) {
  const header = [...labelHeaders, ...monthDays.map((d) => `${d.label} ${d.day}`), 'Total (h)', ...extraHeaders];
  const data = rows.map((r) => {
    const total = Object.values(r.days).reduce((a, b) => a + b, 0);
    return [...r.labels, ...monthDays.map((d) => toHours(r.days[d.day])), toHours(total), ...extraHeaders.map((h) => toHours(r.extra?.[h]))];
  });
  const dayTotals = monthDays.map((d) => toHours(rows.reduce((sum, r) => sum + (r.days[d.day] || 0), 0)));
  const grand = toHours(rows.reduce((sum, r) => sum + Object.values(r.days).reduce((a, b) => a + b, 0), 0));
  const extraTotals = extraHeaders.map((h) => toHours(rows.reduce((sum, r) => sum + (r.extra?.[h] || 0), 0)));
  const totalRow = ['Total', ...labelHeaders.slice(1).map(() => ''), ...dayTotals, grand, ...extraTotals];

  const ws = XLSX.utils.aoa_to_sheet([header, ...data, totalRow]);
  const firstDayCol = labelHeaders.length;
  const lastRow = data.length + 1;
  header.forEach((_, c) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = { fill: FILL_HEADER, font: FONT_HEADER, alignment: { horizontal: 'center' } };
  });
  monthDays.forEach((d, i) => {
    if (!d.weekend) return;
    for (let r = 1; r <= lastRow; r++) {
      const ref = XLSX.utils.encode_cell({ r, c: firstDayCol + i });
      if (!ws[ref]) ws[ref] = { t: 's', v: '' };
      ws[ref].s = { ...(ws[ref].s || {}), fill: FILL_WEEKEND };
    }
  });
  for (let c = 0; c < header.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: lastRow, c });
    if (ws[ref]) ws[ref].s = { ...(ws[ref].s || {}), font: { bold: true } };
  }
  ws['!cols'] = [...labelHeaders.map(() => ({ wch: 32 })), ...monthDays.map(() => ({ wch: 6 })), { wch: 9 }, ...extraHeaders.map(() => ({ wch: 11 }))];
  return ws;
}

/** Scarica le ore del mese: foglio per progetto e, se presente, foglio per persona. */
export function downloadHoursWorkbook({ year, month, monthDays, projectRows, peopleRows }) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSheet(['Project', 'Customer'], projectRows, monthDays), 'By project');
  if (peopleRows?.length) {
    XLSX.utils.book_append_sheet(wb, buildSheet(['Project', 'Person', 'Team'], peopleRows, monthDays), 'By person');
  }
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `hours_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
}

/** Scarica le ore del mese per persona con le ore mancanti fino a ieri. */
export function downloadPeopleHoursWorkbook({ year, month, monthDays, peopleRows }) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSheet(['Person', 'People Manager'], peopleRows, monthDays, ['Missing (h)']), 'By person');
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `people_hours_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
}
